import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card, Typography, Row, Col, Statistic, Spin, message, Table, Tag,
  Select, Space, Button, Alert, List, Tooltip, Badge, Empty
} from 'antd';
import {
  ShopOutlined, CheckCircleOutlined, CloseCircleOutlined,
  SwapOutlined, ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const VendorComparison = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [vendorProducts, setVendorProducts] = useState({});
  const [loadingProducts, setLoadingProducts] = useState({});

  // Define fetchVendorProducts before useEffects that use it
  const fetchVendorProducts = useCallback(async (vendorId) => {
    setLoadingProducts(prev => {
      if (prev[vendorId]) return prev; // Already loading
      return { ...prev, [vendorId]: true };
    });
    try {
      const params = new URLSearchParams();
      params.append('vendor_id', vendorId);
      params.append('limit', '100');

      const response = await axios.get(
        `${API_BASE_URL}/api/scraping/products/scraped/public?${params.toString()}`
      );
      setVendorProducts(prev => ({ ...prev, [vendorId]: response.data }));
    } catch (error) {
      console.error('Error fetching vendor products:', error);
      message.error('Failed to load vendor products');
    } finally {
      setLoadingProducts(prev => ({ ...prev, [vendorId]: false }));
    }
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/scraping/stats/public`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      message.error('Failed to load vendor list');
    } finally {
      setLoading(false);
    }
  };

  // Load stats (vendor list) on mount
  useEffect(() => {
    fetchStats();
  }, []);

  // Initialize from URL params
  useEffect(() => {
    const vendorsParam = searchParams.get('vendors');
    if (vendorsParam && stats) {
      const vendorIds = vendorsParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      setSelectedVendors(vendorIds);
    }
  }, [searchParams, stats]);

  // Fetch products when selected vendors change
  useEffect(() => {
    selectedVendors.forEach(vendorId => {
      if (!vendorProducts[vendorId]) {
        fetchVendorProducts(vendorId);
      }
    });
  }, [selectedVendors, fetchVendorProducts, vendorProducts]);

  const handleVendorSelect = (vendorIds) => {
    setSelectedVendors(vendorIds);
    // Update URL
    if (vendorIds.length > 0) {
      setSearchParams({ vendors: vendorIds.join(',') });
    } else {
      setSearchParams({});
    }
  };

  const handleReset = () => {
    setSelectedVendors([]);
    setSearchParams({});
  };

  // Get all unique categories across selected vendors
  const allCategories = useMemo(() => {
    const categories = new Set();
    selectedVendors.forEach(vendorId => {
      const products = vendorProducts[vendorId] || [];
      products.forEach(p => {
        if (p.category) categories.add(p.category);
      });
    });
    return Array.from(categories).sort();
  }, [selectedVendors, vendorProducts]);

  // Get all unique product names across selected vendors
  const allProductNames = useMemo(() => {
    const names = new Set();
    selectedVendors.forEach(vendorId => {
      const products = vendorProducts[vendorId] || [];
      products.forEach(p => {
        if (p.name) names.add(p.name.toLowerCase());
      });
    });
    return Array.from(names);
  }, [selectedVendors, vendorProducts]);

  // Comparison matrix - which vendors have which products (by name)
  const comparisonMatrix = useMemo(() => {
    const matrix = {};
    allProductNames.forEach(name => {
      matrix[name] = {};
      selectedVendors.forEach(vendorId => {
        const products = vendorProducts[vendorId] || [];
        const match = products.find(p => p.name?.toLowerCase() === name);
        matrix[name][vendorId] = match || null;
      });
    });
    return matrix;
  }, [allProductNames, selectedVendors, vendorProducts]);

  // Category comparison
  const categoryComparison = useMemo(() => {
    const comparison = {};
    allCategories.forEach(cat => {
      comparison[cat] = {};
      selectedVendors.forEach(vendorId => {
        const products = vendorProducts[vendorId] || [];
        const count = products.filter(p => p.category === cat).length;
        comparison[cat][vendorId] = count;
      });
    });
    return comparison;
  }, [allCategories, selectedVendors, vendorProducts]);

  // Get vendor name by ID (memoized for stable reference)
  const getVendorName = useMemo(() => {
    return (vendorId) => {
      if (!stats?.vendors) return `Vendor ${vendorId}`;
      const vendor = stats.vendors.find(v => v.id === vendorId);
      return vendor?.name || `Vendor ${vendorId}`;
    };
  }, [stats]);

  // Stats for comparison
  const comparisonStats = useMemo(() => {
    if (selectedVendors.length < 2) return null;

    let sharedProducts = 0;
    let uniqueProducts = 0;

    Object.values(comparisonMatrix).forEach(vendorMap => {
      const hasCount = Object.values(vendorMap).filter(v => v !== null).length;
      if (hasCount > 1) {
        sharedProducts++;
      } else if (hasCount === 1) {
        uniqueProducts++;
      }
    });

    return {
      totalProducts: allProductNames.length,
      sharedProducts,
      uniqueProducts,
      categories: allCategories.length
    };
  }, [selectedVendors, comparisonMatrix, allProductNames, allCategories]);

  // Columns for comparison table
  const comparisonColumns = useMemo(() => {
    const cols = [
      {
        title: 'Product',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        width: 200,
        render: (name, record) => (
          <Space direction="vertical" size={0}>
            <Text strong style={{ textTransform: 'capitalize' }}>{name}</Text>
            {record.category && (
              <Tag color="blue" style={{ fontSize: 11 }}>{record.category}</Tag>
            )}
          </Space>
        ),
      },
    ];

    selectedVendors.forEach(vendorId => {
      cols.push({
        title: (
          <Space>
            <ShopOutlined />
            {getVendorName(vendorId)}
          </Space>
        ),
        dataIndex: ['vendors', vendorId],
        key: vendorId,
        width: 180,
        align: 'center',
        render: (product) => {
          if (product) {
            return (
              <Tooltip title={product.description || 'Available'}>
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
              </Tooltip>
            );
          }
          return (
            <CloseCircleOutlined style={{ color: '#d9d9d9', fontSize: 18 }} />
          );
        },
      });
    });

    return cols;
  }, [selectedVendors, getVendorName]);

  // Data for comparison table
  const comparisonData = useMemo(() => {
    return allProductNames.map(name => {
      const vendors = comparisonMatrix[name];
      // Get first non-null product for category
      const firstProduct = Object.values(vendors).find(v => v !== null);
      return {
        key: name,
        name,
        category: firstProduct?.category,
        vendors,
      };
    }).sort((a, b) => {
      // Sort by: shared first, then by name
      const aShared = Object.values(a.vendors).filter(v => v !== null).length;
      const bShared = Object.values(b.vendors).filter(v => v !== null).length;
      if (aShared !== bShared) return bShared - aShared;
      return a.name.localeCompare(b.name);
    });
  }, [allProductNames, comparisonMatrix]);

  // Category columns
  const categoryColumns = useMemo(() => {
    const cols = [
      {
        title: 'Category',
        dataIndex: 'category',
        key: 'category',
        width: 200,
        render: (cat) => <Tag color="green">{cat}</Tag>,
      },
    ];

    selectedVendors.forEach(vendorId => {
      cols.push({
        title: getVendorName(vendorId),
        dataIndex: ['counts', vendorId],
        key: vendorId,
        width: 150,
        align: 'center',
        render: (count) => (
          <Badge
            count={count || 0}
            showZero
            style={{ backgroundColor: count > 0 ? '#52c41a' : '#d9d9d9' }}
          />
        ),
      });
    });

    return cols;
  }, [selectedVendors, getVendorName]);

  // Category data
  const categoryData = useMemo(() => {
    return allCategories.map(cat => ({
      key: cat,
      category: cat,
      counts: categoryComparison[cat],
    }));
  }, [allCategories, categoryComparison]);

  const isLoading = loading || selectedVendors.some(id => loadingProducts[id]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={2}>
            <SwapOutlined style={{ marginRight: 12 }} />
            Vendor Comparison
          </Title>
          <Text type="secondary">
            Compare products and capabilities across multiple vendors
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={handleReset}>
          Reset
        </Button>
      </div>

      {/* Vendor Selection */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Select
              mode="multiple"
              placeholder="Type to search vendors..."
              style={{ width: '100%' }}
              size="large"
              value={selectedVendors}
              onChange={handleVendorSelect}
              showSearch
              filterOption={(input, option) => {
                const vendorName = option?.['data-name'] || '';
                return vendorName.toLowerCase().includes(input.toLowerCase());
              }}
              loading={loading}
            >
              {stats?.vendors
                ?.filter(v => v.count > 0)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(vendor => (
                  <Option key={vendor.id} value={vendor.id} data-name={vendor.name}>
                    <Space>
                      <ShopOutlined />
                      {vendor.name}
                      <Tag>{vendor.count} products</Tag>
                    </Space>
                  </Option>
                ))}
            </Select>
          </Col>
        </Row>
        {selectedVendors.length < 2 && (
          <Alert
            message="Select at least 2 vendors to compare"
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      {/* Loading State */}
      {isLoading && selectedVendors.length > 0 && (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading vendor products...</div>
        </div>
      )}

      {/* Comparison Results */}
      {!isLoading && selectedVendors.length >= 2 && (
        <>
          {/* Summary Stats */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card size="small">
                <Statistic
                  title="Vendors Compared"
                  value={selectedVendors.length}
                  prefix={<ShopOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card size="small">
                <Statistic
                  title="Total Products"
                  value={comparisonStats?.totalProducts || 0}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card size="small">
                <Statistic
                  title="Shared Products"
                  value={comparisonStats?.sharedProducts || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card size="small">
                <Statistic
                  title="Unique Products"
                  value={comparisonStats?.uniqueProducts || 0}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Category Comparison */}
          <Card title="Category Comparison" style={{ marginBottom: 24 }}>
            <Table
              columns={categoryColumns}
              dataSource={categoryData}
              rowKey="category"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </Card>

          {/* Product Comparison */}
          <Card title={`Product Comparison (${comparisonData.length} products)`}>
            <Alert
              message={
                <span>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                  Products available from a vendor are marked with a green check.
                  Products at the top are shared across more vendors.
                </span>
              }
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Table
              columns={comparisonColumns}
              dataSource={comparisonData}
              rowKey="key"
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 'max-content' }}
              size="small"
            />
          </Card>
        </>
      )}

      {/* Empty State */}
      {!isLoading && selectedVendors.length === 0 && (
        <Card>
          <Empty
            description={
              <span>
                Select vendors above to start comparing their products
              </span>
            }
          />
        </Card>
      )}

      {/* Single Vendor Selected */}
      {!isLoading && selectedVendors.length === 1 && (
        <Card>
          <Alert
            message="Add more vendors to compare"
            description="Select at least one more vendor to see a side-by-side comparison"
            type="info"
            showIcon
          />
          {vendorProducts[selectedVendors[0]] && (
            <div style={{ marginTop: 24 }}>
              <Title level={4}>{getVendorName(selectedVendors[0])} Products</Title>
              <List
                grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4 }}
                dataSource={vendorProducts[selectedVendors[0]]}
                renderItem={product => (
                  <List.Item>
                    <Card size="small">
                      <Text strong>{product.name}</Text>
                      {product.category && (
                        <div>
                          <Tag color="blue" style={{ marginTop: 4 }}>{product.category}</Tag>
                        </div>
                      )}
                    </Card>
                  </List.Item>
                )}
              />
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default VendorComparison;
